import type { IdentityConflictRow, IdentityMismatchField } from "@repo/shared/types";
import { AlertTriangle, Check } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./identity-conflict-panel.i18n";

type IdentityConflictPanelProps = {
  rows: IdentityConflictRow[];
  onResolve: (field: IdentityMismatchField, decision: "update" | "dismiss") => Promise<void>;
  disabled?: boolean;
};

/**
 * QL-f — the scanned document disagrees with our record, so ask.
 *
 * Shown identically on the advisor page and the public client page, because the
 * question is the same one and a client who has just scanned their passport is
 * the better source. What differs is only what gets RECORDED — a public answer
 * carries no user, which the compliance log shows.
 *
 * Both answers resolve, and neither is preselected: correcting the record and
 * accepting the discrepancy are both legitimate (a married name, a typo, a
 * passport that predates a name change), and guessing which one the person
 * meant is exactly what this panel exists to stop.
 */
export function IdentityConflictPanel({ rows, onResolve, disabled }: IdentityConflictPanelProps) {
  const t = useT(T);
  const [pending, setPending] = useState<IdentityMismatchField | null>(null);
  const [failed, setFailed] = useState<IdentityMismatchField | null>(null);

  if (rows.length === 0) return null;

  const labelFor = (field: IdentityMismatchField) =>
    field === "name" ? t.fieldName : field === "dob" ? t.fieldDob : t.fieldCountry;

  const answer = async (field: IdentityMismatchField, decision: "update" | "dismiss") => {
    setPending(field);
    setFailed(null);
    try {
      await onResolve(field, decision);
    } catch {
      // Without this the rejection escaped the click handler unhandled: the
      // buttons simply stopped responding and the flow stayed blocked with
      // nothing on screen to say why.
      setFailed(field);
    } finally {
      setPending(null);
    }
  };

  const unanswered = rows.filter((row) => !row.resolved).length;

  return (
    <Alert variant={unanswered > 0 ? "warning" : "default"} className="mt-4">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription>
        <div className="space-y-3">
          <div>
            <p className="font-medium text-sm">{t.title}</p>
            <p className="text-xs text-muted-foreground">{t.description}</p>
          </div>

          <ul className="divide-y">
            {rows.map((row) => (
              <li key={row.field} className="space-y-2 py-3 first:pt-0 last:pb-0">
                <p className="font-medium text-sm">{labelFor(row.field)}</p>

                {/* Ours on the left, the document on the right — the layout the
                    question is actually asked in. */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">{t.ours}</p>
                    <p className="text-sm">{row.ours || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t.theirs}</p>
                    <p className="text-sm font-medium">{row.theirs || "—"}</p>
                  </div>
                </div>

                {row.resolved ? (
                  <p className="flex items-center gap-1 text-xs text-success">
                    <Check className="h-3 w-3" aria-hidden="true" />
                    {t.answered}
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="default"
                      disabled={disabled || pending === row.field}
                      onClick={() => answer(row.field, "update")}
                    >
                      {t.update}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={disabled || pending === row.field}
                      onClick={() => answer(row.field, "dismiss")}
                    >
                      {t.ignore}
                    </Button>
                    {failed === row.field && (
                      <span className="text-destructive text-xs">{t.failed}</span>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {unanswered > 0 && <p className="text-xs font-medium">{t.blocking}</p>}
        </div>
      </AlertDescription>
    </Alert>
  );
}
